import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FileText, ChevronsUpDown } from 'lucide-react'
import { cn } from '@/lib/utils/helpers'
import parityStyles from '@/features/invitee/styles/invitesParity.module.css'
import configSortStyles from '@/components/listing/propertySortHeaderButton.module.css'
import {
  primaryEntityActionButtonClass,
  secondaryEntityActionButtonClass,
} from '@/components/ui/entityActionButtonClasses'
import { showSuccess, showApiError } from '@/lib/utils/notifications'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { PageShell } from '@/components/layout/PageShell'
import { ListingTableLoadingRow } from '@/components/ui/ModuleScreenLoading'
import { ListPaginationBar } from '@/components/ui/ListPaginationBar'
import {
  ListingDataTableCard,
  ListingDataTableScroll,
  LISTING_DATA_TABLE_CLASS,
  } from '@/components/ui/listingDataTable'
import { StockAddModal } from '../components/StockAddModal'
import { StockDateEditModal } from '../components/StockDateEditModal'
import { StockLogsModal } from '../components/StockLogsModal'
import { getBrowserApiBaseUrl } from '@/config/env'
import { getAuthTokenFromStorage } from '@/lib/utils/storage'
import { useUserRole } from '@/hooks/useUserRole'
import {
  addStock,
  getStocks,
  revokeStocks,
  searchCompaniesForStock,
  updateStockDates,
  type StockRegistryCompanyOption,
} from '../services/stockRegistryService'
import type {
  Stock,
  StockAddPayload,
  StockDateType,
  StockDateUpdatePayload,
  StockListParams,
  StockSortKey,
  SortDirection,
} from '../types'
import { openStockCertificatePreview } from '../utils/openStockCertificatePreview'

const DEFAULT_PARAMS: StockListParams = {
  page: 1,
  perPage: 15,
  sortBy: 'desc',
  orderBy: 'stock_id',
}

const PER_PAGE_OPTIONS = [15, 30, 50, 100] as const

function formatDate(value: string | undefined): string {
  if (!value) return '—'
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return value
  return `${match[2]}/${match[3]}/${match[1].slice(-2)}`
}

function isRevoked(stock: Stock): boolean {
  const v = stock.is_revoke
  if (v === true || v === 1 || v === '1') return true
  if (v === false || v === 0 || v === '0') return false
  if (v === undefined) return false
  return String(v) === '1'
}

function buildExportUrl(listParams: StockListParams): string {
  const base = getBrowserApiBaseUrl()
  const token = getAuthTokenFromStorage()
  const p = new URLSearchParams()
  p.set('page', String(listParams.page ?? 1))
  p.set('perPage', String(listParams.perPage ?? 15))
  p.set('sortBy', listParams.sortBy ?? 'desc')
  p.set('orderBy', listParams.orderBy ?? 'stock_id')
  if (listParams.q?.trim()) p.set('q', listParams.q.trim())
  if (listParams.company_id) p.set('company_id', String(listParams.company_id))
  if (listParams.is_revoke) p.set('is_revoke', listParams.is_revoke)
  if (token) p.set('access_token', token)
  p.set('view', 'xlsx')
  return `${base}/api/export/stock?${p.toString()}`
}

export function StockRegistryListPage() {
  const { hasPolicy, roleName } = useUserRole()
  const canRevokeColumn = hasPolicy(['stock_revoke_certificate'])
  const canEditIssued = hasPolicy(['stock_issued_date'])
  const canEditEffective = hasPolicy(['stock_effective_date'])
  const showLogsColumn = roleName === 'ROLE_ADMIN' || roleName === 'ROLE_EXECS'

  const [stocks, setStocks] = useState<Stock[]>([])
  const [params, setParams] = useState<StockListParams>(DEFAULT_PARAMS)
  const [pagination, setPagination] = useState({ current: 1, last: 1, total: 0 })
  const [loading, setLoading] = useState(false)
  const [selected, setSelected] = useState<Set<number>>(new Set())

  const [companyFilter, setCompanyFilter] = useState<string>('')
  const [revokeFilter, setRevokeFilter] = useState<'' | '1'>('')
  const [companies, setCompanies] = useState<StockRegistryCompanyOption[]>([])

  const [addOpen, setAddOpen] = useState(false)
  const [addSubmitting, setAddSubmitting] = useState(false)

  const [dateEdit, setDateEdit] = useState<{ stock: Stock; type: StockDateType } | null>(null)
  const [dateEditSubmitting, setDateEditSubmitting] = useState(false)

  const [logsStockId, setLogsStockId] = useState<number | null>(null)

  const [revokeConfirm, setRevokeConfirm] = useState(false)
  const [openingCertificateStockId, setOpeningCertificateStockId] = useState<number | null>(null)

  const mountedRef = useRef(false)

  const colCount = useMemo(() => {
    let n = 9
    if (canRevokeColumn) n += 1
    if (showLogsColumn) n += 1
    return n
  }, [canRevokeColumn, showLogsColumn])

  const exportHref = useMemo(() => buildExportUrl(params), [params])

  const load = useCallback(async (next: StockListParams) => {
    setLoading(true)
    try {
      const response = await getStocks(next)
      setStocks(response.data ?? [])
      setPagination({
        current: response.current ?? 1,
        last: response.last ?? 1,
        total: response.total ?? 0,
      })
      setSelected(new Set())
    } catch (error) {
      showApiError(error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (mountedRef.current) return
    mountedRef.current = true
    void load(DEFAULT_PARAMS)
    searchCompaniesForStock()
      .then((opts) => {
        setCompanies(opts)
        if (opts.length === 1) {
          setCompanyFilter(String(opts[0].value))
        }
      })
      .catch(showApiError)
  }, [load])

  const applyParams = useCallback(
    (next: StockListParams) => {
      setParams(next)
      void load(next)
    },
    [load],
  )

  const toggleSort = (key: StockSortKey) => {
    const nextDir: SortDirection =
      params.orderBy === key && params.sortBy === 'asc' ? 'desc' : 'asc'
    applyParams({ ...params, orderBy: key, sortBy: nextDir, page: 1 })
  }

  const submitFilter = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const next: StockListParams = { ...params, page: 1 }
    delete next.company_id
    delete next.is_revoke
    if (companyFilter) next.company_id = companyFilter
    if (revokeFilter) next.is_revoke = revokeFilter
    applyParams(next)
  }

  const resetFilter = () => {
    setCompanyFilter('')
    setRevokeFilter('')
    const next = { ...params }
    delete next.company_id
    delete next.is_revoke
    next.page = 1
    applyParams(next)
  }

  const goToPage = (pageNo: number) => applyParams({ ...params, page: pageNo })

  const changePerPage = (perPage: number) => {
    applyParams({ ...params, perPage, page: 1 })
  }

  const toggleSelect = (stockId: number) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(stockId)) next.delete(stockId)
      else next.add(stockId)
      return next
    })
  }

  const handleAdd = async (payload: StockAddPayload) => {
    setAddSubmitting(true)
    try {
      await addStock(payload)
      showSuccess('Stock Added Successfully!')
      setAddOpen(false)
      void load(params)
    } catch (error) {
      showApiError(error)
    } finally {
      setAddSubmitting(false)
    }
  }

  const handleDateUpdate = async (stockId: number, payload: StockDateUpdatePayload) => {
    setDateEditSubmitting(true)
    try {
      await updateStockDates(stockId, payload)
      showSuccess('Stock Date Updated Successfully!')
      setDateEdit(null)
      void load(params)
    } catch (error) {
      showApiError(error)
    } finally {
      setDateEditSubmitting(false)
    }
  }

  const handleRevoke = async () => {
    if (selected.size === 0) return
    try {
      await revokeStocks(Array.from(selected))
      showSuccess('Certificate Revoke Successfully')
      setRevokeConfirm(false)
      void load(params)
    } catch (error) {
      showApiError(error)
    }
  }

  const handleViewCertificate = async (stock: Stock) => {
    const path = stock.certificate_path ?? stock.certificate
    if (!path) return
    setOpeningCertificateStockId(stock.stock_id)
    try {
      await openStockCertificatePreview(stock.stock_id, path)
    } finally {
      setOpeningCertificateStockId(null)
    }
  }

  const lastPage = Math.max(1, pagination.last)
  const currentPage = Math.min(Math.max(1, pagination.current), lastPage)
  const perPage = params.perPage ?? 15
  const showPaginationBar = pagination.total > 0

  const SortHeader = ({ column, label }: { column: StockSortKey; label: string }) => {
    const isActive = params.orderBy === column
    return (
      <th
        className="select-none cursor-pointer !text-left"
        onClick={() => toggleSort(column)}
      >
        <button
          type="button"
          className={cn(parityStyles.sortHeaderButton, configSortStyles.propertySortHeaderButton, 'justify-start')}
          onClick={(event) => {
            event.stopPropagation()
            toggleSort(column)
          }}
        >
          <span className={parityStyles.sortHeaderLabel}>{label}</span>
          <span
            aria-hidden="true"
            className={cn(parityStyles.sortIcon, isActive && parityStyles.sortIconActive)}
          >
            {isActive ? (params.sortBy === 'asc' ? '▲' : '▼') : <ChevronsUpDown className="h-3 w-3" />}
          </span>
        </button>
      </th>
    )
  }

  return (
    <PageShell title="STOCK REGISTER">
      <div className="space-y-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <form onSubmit={submitFilter} className="flex flex-wrap items-end gap-2">
            <div className="w-64 min-w-[240px]">
              <label className="mb-1 block text-xs font-medium text-slate-500">Company</label>
              <select
                value={companyFilter}
                onChange={(e) => setCompanyFilter(e.target.value)}
                className="w-full min-w-0 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-button"
              >
                <option value="">All Company</option>
                {companies.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-[140px]">
              <label className="mb-1 block text-xs font-medium text-slate-500">Status</label>
              <select
                value={revokeFilter}
                onChange={(e) => setRevokeFilter(e.target.value as '' | '1')}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-button"
              >
                <option value="">Active</option>
                <option value="1">Not Active</option>
              </select>
            </div>
            <button
              type="submit"
              className={cn(primaryEntityActionButtonClass, 'px-4 py-2 text-sm font-medium')}
            >
              Done
            </button>
            <button
              type="button"
              onClick={resetFilter}
              className={cn(secondaryEntityActionButtonClass, 'px-4 py-2 text-sm font-medium')}
            >
              Reset All
            </button>
          </form>

          <div className="flex flex-wrap items-center justify-end gap-2">
            {stocks.length > 0 && (
              <a
                href={exportHref}
                className={cn(primaryEntityActionButtonClass, 'px-4 py-2 text-sm font-medium')}
              >
                Export
              </a>
            )}
            {canRevokeColumn && (
              <button
                type="button"
                onClick={() => setRevokeConfirm(true)}
                disabled={selected.size === 0}
                className={cn(secondaryEntityActionButtonClass, 'px-4 py-2 text-sm font-medium border-button text-button hover:bg-button-soft')}
              >
                Revoke Certificate
              </button>
            )}
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              className={cn(primaryEntityActionButtonClass, 'px-4 py-2 text-sm font-medium')}
            >
              Add Certificate
            </button>
          </div>
        </div>

        <ListingDataTableCard>
          {showPaginationBar && (
            <ListPaginationBar
              placement="header"
              testIdPrefix="stock-registry"
              currentPage={currentPage}
              lastPage={lastPage}
              totalRecords={pagination.total}
              perPage={perPage}
              onPageChange={goToPage}
              onPerPageChange={changePerPage}
              perPageOptions={PER_PAGE_OPTIONS}
            />
          )}

          <ListingDataTableScroll>
            <table className={cn(parityStyles.propertyListingTable, parityStyles.listingTable, LISTING_DATA_TABLE_CLASS)}>
              <thead className="border-b border-[#c8d7e8] bg-[#dfeaf5] text-slate-900">
                <tr>
                  <SortHeader column="company_name" label="Company Name" />
                  <SortHeader column="issued_date" label="Stock Issued Date" />
                  <SortHeader column="effective_date" label="Stock Effective Date" />
                  <SortHeader column="is_revoke" label="Active" />
                  <SortHeader column="stock_class" label="Class" />
                  <SortHeader column="stock_id" label="Certificate Number" />
                  <SortHeader column="share_count" label="Shares Issued" />
                  <th className="px-4 py-3 !text-left">Certificate</th>
                  {canRevokeColumn && (
                    <th className="px-4 py-3 !text-center">Revoke Certificate</th>
                  )}
                  <th className="px-4 py-3 !text-left">Revocation Date</th>
                  {showLogsColumn && <th className="px-4 py-3 !text-left">Logs</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#dfe7f0] [&>tr:nth-child(even)]:bg-[#f2f7fd]">
                {loading ? (
                  <ListingTableLoadingRow colSpan={colCount} />
                ) : stocks.length === 0 ? (
                  <tr>
                    <td colSpan={colCount} className="px-4 py-8 text-center text-slate-500">
                      No stock certificates found.
                    </td>
                  </tr>
                ) : (
                  stocks.map((stock) => {
                    const revoked = isRevoked(stock)
                    return (
                      <tr key={stock.stock_id} className={cn(parityStyles.listingTableRow, revoked && '!bg-slate-50')}>
                        <td className="px-4 py-3 text-slate-700">{stock.company_name ?? '—'}</td>
                        <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                          {canEditIssued && !revoked ? (
                            <button
                              type="button"
                              onClick={() => setDateEdit({ stock, type: 'stock_issued_date' })}
                              className="text-button underline decoration-dotted hover:text-button-hover"
                            >
                              {formatDate(stock.issued_date)}
                            </button>
                          ) : (
                            <span className={!revoked ? 'text-button' : ''}>
                              {formatDate(stock.issued_date)}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                          {canEditEffective && !revoked ? (
                            <button
                              type="button"
                              onClick={() => setDateEdit({ stock, type: 'stock_effective_date' })}
                              className="text-button underline decoration-dotted hover:text-button-hover"
                            >
                              {formatDate(stock.effective_date)}
                            </button>
                          ) : (
                            <span className={!revoked ? 'text-button' : ''}>
                              {formatDate(stock.effective_date)}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-700">{revoked ? 'No' : 'Yes'}</td>
                        <td className="px-4 py-3 text-slate-700">{stock.stock_class ?? '—'}</td>
                        <td className="px-4 py-3 font-medium text-slate-900">{stock.stock_id}</td>
                        <td className="px-4 py-3 text-slate-700">{stock.share_count ?? '—'}</td>
                        <td className="px-4 py-3">
                          {stock.certificate_path || stock.certificate ? (
                            <button
                              type="button"
                              onClick={() => void handleViewCertificate(stock)}
                              disabled={openingCertificateStockId === stock.stock_id}
                              className="inline-flex items-center gap-1 text-xs text-button underline hover:text-button-hover disabled:opacity-50"
                              data-testid={`stock-registry-cert-view-${stock.stock_id}`}
                            >
                              <FileText className="h-3 w-3 shrink-0" aria-hidden />
                              View
                            </button>
                          ) : (
                            '—'
                          )}
                        </td>
                        {canRevokeColumn && (
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center">
                              {!revoked ? (
                                <input
                                  type="checkbox"
                                  checked={selected.has(stock.stock_id)}
                                  onChange={() => toggleSelect(stock.stock_id)}
                                  aria-label="Select for revoke"
                                  className="h-4 w-4 rounded border-slate-300 text-button accent-button focus:ring-button"
                                />
                              ) : (
                                <span className="text-slate-300">—</span>
                              )}
                            </div>
                          </td>
                        )}
                        <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                          {revoked ? formatDate(stock.revoke_date) : '—'}
                        </td>
                        {showLogsColumn && (
                          <td className="px-4 py-3">
                            <button
                              type="button"
                              onClick={() => setLogsStockId(stock.stock_id)}
                              className="text-xs text-button underline hover:text-button-hover"
                            >
                              View
                            </button>
                          </td>
                        )}
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </ListingDataTableScroll>

          {showPaginationBar && (
            <ListPaginationBar
              placement="footer"
              testIdPrefix="stock-registry"
              currentPage={currentPage}
              lastPage={lastPage}
              totalRecords={pagination.total}
              perPage={perPage}
              onPageChange={goToPage}
              onPerPageChange={changePerPage}
              perPageOptions={PER_PAGE_OPTIONS}
            />
          )}
        </ListingDataTableCard>
      </div>

      <StockAddModal
        isOpen={addOpen}
        submitting={addSubmitting}
        onClose={() => setAddOpen(false)}
        onSubmit={handleAdd}
      />

      <StockDateEditModal
        isOpen={dateEdit !== null}
        stock={dateEdit?.stock ?? null}
        dateType={dateEdit?.type ?? null}
        submitting={dateEditSubmitting}
        onClose={() => setDateEdit(null)}
        onSubmit={handleDateUpdate}
      />

      <StockLogsModal
        isOpen={logsStockId !== null}
        stockId={logsStockId}
        onClose={() => setLogsStockId(null)}
      />

      <ConfirmDialog
        isOpen={revokeConfirm}
        title="Revoke Certificate"
        message={`Are you sure want to revoke certificate of selected ${selected.size} company`}
        primaryLabel="Revoke"
        onPrimary={handleRevoke}
        secondaryLabel="Cancel"
        onSecondary={() => setRevokeConfirm(false)}
        variant="warning"
      />
    </PageShell>
  )
}
